import {
  categoryNeedsFromExpense,
  ensureCategoriesForSync,
  prepareExpenseForSync,
  resolveDeferredPayee,
} from "./offline-associated";
import { canChangePending, migratePendingEntries, pendingChanged, readPendingEntries } from "./pending-entries";
import { syncPendingReceipts } from "./pending-receipts";
import { syncPendingRecords } from "./pending-records";
import { runBatch } from "../features/entry/batch-save";

let running: Promise<{ confirmed: number; errors: string[] }> | undefined;

/** One dispatcher for form saves, reconnects and retries; Web Locks also serialize browser tabs. */
export function syncPendingEntries(request: typeof fetch = fetch): Promise<{ confirmed: number; errors: string[] }> {
  if (running) return running;
  const run = async () => {
    let confirmed = 0;
    const errors: string[] = [];
    migratePendingEntries();
    for (const { key } of readPendingEntries()) {
      if (typeof navigator !== "undefined" && !navigator.onLine) break;
      const entry = readPendingEntries().find(item => item.key === key)?.entry;
      if (!entry) continue;
      if (entry.progress && canChangePending(key, entry)) {
        errors.push(entry.progress.error || "Review this entry before syncing.");
        continue;
      }
      try {
        await ensureCategoriesForSync(categoryNeedsFromExpense(entry), request);
        const expense = prepareExpenseForSync(entry);
        const [result] = await runBatch(
          [entry.progress?.expense
            ? { ...entry.progress, expense: prepareExpenseForSync(entry.progress.expense) }
            : { expense, status: "pending" }],
          ([progress]) => {
            localStorage.setItem(key, JSON.stringify({ ...entry, progress }));
            pendingChanged();
          },
          request,
        );
        if (result.status !== "saved") {
          errors.push(result.error || "An entry is still waiting for confirmation.");
          continue;
        }
        if (entry.recurring) {
          const payeeId = await resolveDeferredPayee(
            typeof entry.recurring.payeeId === "string" ? entry.recurring.payeeId : undefined,
            undefined,
            request,
          );
          const response = await request("/api/bills/create", {
            method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...entry.recurring,
              ...(payeeId ? { payeeId } : { payeeId: undefined }),
              sourceTransactionId: result.transactionId,
            }),
            signal: AbortSignal.timeout(45000),
          });
          const body = await response.json() as { id?: string; error?: string };
          if (!response.ok || !body.id) throw new Error(body.error || "Transaction synced; recurring bill is waiting to sync.");
        }
        localStorage.removeItem(key);
        confirmed++;
        pendingChanged();
      } catch (error) {
        errors.push(error instanceof Error ? error.message : "Could not sync an entry. It remains on this device.");
      }
    }
    const records = await syncPendingRecords(request);
    confirmed += records.confirmed;
    errors.push(...records.errors);
    const receipts = await syncPendingReceipts(request);
    confirmed += receipts.confirmed;
    errors.push(...receipts.errors);
    return { confirmed, errors };
  };
  running = (async () => {
    // Defer work until the shared promise has been assigned.
    await Promise.resolve();
    if (typeof navigator !== "undefined" && navigator.locks)
      return await navigator.locks.request("barnyard-pending-sync", run);
    return await run();
  })().finally(() => { running = undefined; });
  return running;
}
